import React, { useEffect, useState } from 'react';
import { Card, Button, Row, Col } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { uuidv4 } from '@awp/helpers/Utils';
import AlertCondition from './AlertCondition';
import { FormRadio } from '../Common/AwpFormInputs';
import { selectObservationTypes as selectObservationTypesDdlb } from '@stores/dropdownsSlice';
import { selectObservationTypes } from '@awp/stores/dataVisualizationSlice';
import { AllOperators } from '@awp/constants';
import { useSelector } from 'react-redux';

const AlertGroup = ({
  level,
  name,
  onDelete,
  groupName,
  setFieldValue,
  values,
  dynamicFields,
  setDynamicFields,
  groupData,
  props,
}) => {
  const [subGroups, setSubGroups] = useState([]);
  const [conditions, setConditions] = useState([]);
  const observationTypes = useSelector(selectObservationTypes);
  const observationTypesDdlb = useSelector(selectObservationTypesDdlb);

  useEffect(() => {
    if (groupData) {
      setFieldValue(`TriggerGroup.groupId_${groupName}_${name}`, groupData.TriggerGroupId ?? 0);
      setFieldValue(`TriggerGroup.isAnyCondition_${groupName}_${name}`, groupData.IsAnyCondition);
      handleAddConditions(groupData.TriggerConditions);

      const groups = subGroups;
      groupData.ChildTriggerGroups.forEach((g) => {
        groups.push(createGroup(g));
      });
      setSubGroups([...groups]);
    }
  }, [groupData]);

  const createGroup = (groupData) => {
    const childKey = uuidv4().replace(/-/g, '');
    const nextGroupIndex = getNextGroupIndex(subGroups);
    return { level: level + 1, name: childKey, groupName: `${groupName}S${nextGroupIndex}`, groupData };
  };

  const getNextGroupIndex = (subgroups) => {
    if (subgroups.length === 0) {
      return '1';
    }

    const lastGroupName = subgroups[subgroups.length - 1].groupName;
    const parts = lastGroupName.split('S');
    const lastIndex = parts[parts.length - 1];
    const nextIndex = (parseInt(lastIndex) + 1).toString();
    return nextIndex;
  };

  const handleAddConditions = (triggerConditions) => {
    if (!triggerConditions) return;

    const conditionNames = [];
    const fieldNames = [];

    triggerConditions.forEach((c) => {
      const conditionName = uuidv4().replace(/-/g, '');
      conditionNames.push(conditionName);

      const idField = `conditionId_${groupName}_${conditionName}`;
      const observationTypeField = `observationType_${groupName}_${conditionName}`;
      const operatorField = `operator_${groupName}_${conditionName}`;
      const valueField = `value_${groupName}_${conditionName}`;
      const dataTypeField = `dataType_${groupName}_${conditionName}`;

      fieldNames.push(observationTypeField);
      fieldNames.push(operatorField);
      fieldNames.push(valueField);
      fieldNames.push(dataTypeField);

      setFieldValue(
        `TriggerGroup.${observationTypeField}`,
        observationTypesDdlb.options.find((x) => x.value === c.ObservationTypeId),
      );
      setFieldValue(
        `TriggerGroup.${operatorField}`,
        AllOperators.find((x) => x.value === c.ComparisonOperator),
      );
      setFieldValue(`TriggerGroup.${valueField}`, c.ComparisonValue);
      setFieldValue(`TriggerGroup.${idField}`, c.TriggerConditionId ?? 0);
      setFieldValue(
        `TriggerGroup.${dataTypeField}`,
        observationTypes.find((x) => x.ObservationTypeId === c.ObservationTypeId)?.DataType,
      );
    });

    setConditions([...conditions, ...conditionNames]);
    setDynamicFields([...dynamicFields, ...fieldNames]);
  };

  const deleteFromDynamicFields = (name) => {
    const fields = dynamicFields.filter((x) => !x.includes(name));
    Object.keys(values.TriggerGroup).forEach((key) => {
      if (key.includes(name)) {
        delete values.TriggerGroup[key];
      }
    });
    setDynamicFields(fields);
  };

  const handleDeleteCondition = (conditionName) => {
    deleteFromDynamicFields(conditionName);
    setConditions(conditions.filter((condition) => condition !== conditionName));
  };

  const onDeleteChild = (name, groupName) => {
    deleteFromDynamicFields(`_${groupName}`);
    setSubGroups(subGroups.filter((child) => child.name !== name));
  };

  return (
    <Card
      style={{
        marginLeft: `${(level - 1) * 1}px`,
        marginTop: '1em',
        border: '1px solid black',
        backgroundColor: `rgb(${250 - level * 20}, ${250 - level * 20}, ${250 - level * 20})`,
      }}
    >
      <Card.Body>
        <Card.Title>
          <Row>
            <Col sm={3} className="form-group">
              <Row>
                <Col>
                  <FormRadio
                    id="And"
                    label="And"
                    name={`TriggerGroup.isAnyCondition_${groupName}_${name}`}
                    value="N"
                    className="my-2 mx-3"
                  />
                </Col>
                <Col>
                  <FormRadio
                    id="Or"
                    label="Or"
                    name={`TriggerGroup.isAnyCondition_${groupName}_${name}`}
                    value="Y"
                    className="my-2 mx-1"
                  />
                </Col>
              </Row>
            </Col>
            <Col sm={9}>
              <Button
                variant="primary"
                onClick={() =>
                  handleAddConditions([{ ObservationTypeId: null, ComparisonOperator: null, ComparisonValue: null }])
                }
                className="mx-1"
              >
                Add Condition
              </Button>
              <Button
                variant="secondary"
                onClick={() =>
                  setSubGroups([
                    ...subGroups,
                    createGroup({
                      TriggerConditions: [{ ObservationTypeId: null, ComparisonOperator: null, ComparisonValue: null }],
                      IsAnyCondition: 'N',
                      ChildTriggerGroups: [],
                    }),
                  ])
                }
                className="mx-1"
              >
                Add Group
              </Button>
              {level != 1 && (
                <Button variant="danger" onClick={() => onDelete(name, groupName)} className="mx-1">
                  Delete Group
                </Button>
              )}
            </Col>
          </Row>
        </Card.Title>
        {conditions.map((condition) => (
          <AlertCondition
            key={condition}
            groupName={groupName}
            conditionName={condition}
            onDeleteCondition={() => handleDeleteCondition(condition)}
            values={values}
            setFieldValue={setFieldValue}
            props={props}
          />
        ))}
        {subGroups &&
          subGroups.map((child, i) => (
            <div key={child.name}>
              <AlertGroup
                key={child.name}
                level={child.level}
                name={child.name}
                onDelete={onDeleteChild}
                groupName={child.groupName}
                setFieldValue={setFieldValue}
                values={values}
                dynamicFields={dynamicFields}
                setDynamicFields={setDynamicFields}
                groupData={child.groupData}
              />
            </div>
          ))}
      </Card.Body>
    </Card>
  );
};

AlertGroup.propTypes = {
  name: PropTypes.string.isRequired,
  level: PropTypes.number.isRequired,
  onDelete: PropTypes.func.isRequired,
  groupName: PropTypes.string,
  dynamicFields: PropTypes.array,
  setDynamicFields: PropTypes.func,
  groupData: PropTypes.object,
};

export default AlertGroup;
